import { useTranslation } from "react-i18next";
import { TextField } from "react-aria-components";
import { Check, ImageIcon, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import type { Category } from "@arc/contracts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface CategoryRowProps {
  category: Category;
  isAttached: boolean;
  busy: boolean;
  editing: boolean;
  editName: string;
  savePending: boolean;
  onEditNameChange: (v: string) => void;
  onToggle: () => void;
  onStartRename: () => void;
  onSaveRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
  /** Open the Edit-category dialog (the ICON editor, ADR-043). */
  onEditIcon: () => void;
}

/**
 * One category row. The WHOLE row is the attach/detach toggle (a single `<button aria-pressed>`); the per-category
 * facility count sits at the right and the icon/rename/delete actions are SIBLINGS of the toggle (never nested — invalid
 * HTML), revealed on hover OR focus-within. The actions are `opacity-0 pointer-events-none` when hidden (NOT
 * `display:none`, so a keyboard user can still focus them to trigger the reveal); their width is reserved so
 * revealing them never reflows the count. Editing swaps the whole row for an inline rename field + Save/Cancel.
 */
export function CategoryRow({
  category,
  isAttached,
  busy,
  editing,
  editName,
  savePending,
  onEditNameChange,
  onToggle,
  onStartRename,
  onSaveRename,
  onCancelRename,
  onDelete,
  onEditIcon,
}: CategoryRowProps) {
  const { t } = useTranslation();
  const count = category.facilityCount ?? 0;

  if (editing) {
    return (
      <li className="flex items-center gap-2 px-3 py-1.5">
        <TextField
          aria-label={t("facilityManagement.categories.renameCategory", { name: category.name })}
          value={editName}
          onChange={onEditNameChange}
          maxLength={255}
          autoFocus
          className="flex-1"
        >
          <Input
            className="h-7"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSaveRename();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                onCancelRename();
              }
            }}
          />
        </TextField>
        <Button variant="ghost" size="icon-sm" aria-label={t("facilityManagement.actions.saveName")} isDisabled={savePending} onPress={onSaveRename}>
          {savePending ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t("facilityManagement.actions.cancelRename")} onPress={onCancelRename}>
          <X className="size-3.5" />
        </Button>
      </li>
    );
  }

  return (
    <li className="group/row hover:bg-muted/50 relative flex items-center transition-colors">
      {/* Whole-row toggle. Accessible name states the action AND the count (so the count isn't lost to SR when the
          visual number is aria-hidden). Right padding clears the count/actions slot. Row-level hover (above) gives a
          full-width highlight between the section's row dividers; the button keeps rounded focus ring only. */}
      <button
        type="button"
        aria-pressed={isAttached}
        aria-label={t(isAttached ? "facilityManagement.categories.toggleDetach" : "facilityManagement.categories.toggleAttach", { name: category.name, count })}
        disabled={busy}
        onClick={onToggle}
        className="focus-visible:ring-ring/50 flex min-w-0 flex-1 items-center gap-2.5 py-2.5 pr-28 pl-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset disabled:opacity-60"
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid size-5 shrink-0 place-items-center rounded-full",
            isAttached ? "bg-primary text-primary-foreground" : "border-border text-muted-foreground border",
          )}
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : isAttached ? <Check className="size-3" /> : <Plus className="size-3" />}
        </span>
        <span className="truncate text-sm">{category.name}</span>
      </button>

      {/* Count columns — facility count + (blank) manual count, aligned under the section's icon headers. An ABSOLUTE,
          pointer-events-none overlay (over the button's reserved pr-24) so the WHOLE row stays a single toggle hit
          target — clicks fall through to the flex-1 button (panel: dead click-zone). Fades out on hover to reveal the
          row actions. aria-hidden: the toggle's accessible name already states the facility count. */}
      <div
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs transition-opacity group-hover/row:opacity-0 group-focus-within/row:opacity-0"
      >
        <span className="w-9 text-center tabular-nums">{count}</span>
        <span className="w-9 text-center">{/* manuals — blank until the manual table lands */}</span>
      </div>

      {/* Row actions — overlay the columns on hover/focus. opacity-0 + pointer-events-none when hidden (NOT display:none)
          so keyboard can still focus them to trigger the reveal. */}
      <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/row:pointer-events-auto group-hover/row:opacity-100 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t(category.imageFileId == null ? "facilityManagement.categories.setIcon" : "facilityManagement.categories.changeIcon", { name: category.name })}
          isDisabled={busy}
          onPress={onEditIcon}
        >
          <ImageIcon className="text-muted-foreground size-3.5" />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t("facilityManagement.categories.renameCategory", { name: category.name })} isDisabled={busy} onPress={onStartRename}>
          <Pencil className="text-muted-foreground size-3.5" />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t("facilityManagement.categories.deleteCategory", { name: category.name })} isDisabled={busy} onPress={onDelete}>
          <Trash2 className="text-muted-foreground size-3.5" />
        </Button>
      </div>
    </li>
  );
}
